'use client';

import React, { useState, useRef, useCallback } from 'react';
import styled from 'styled-components';
import { Modal } from '@/components/modals';
import { Button } from '@/components/ui-components';
import { UploadIcon, CloseIconRed } from '@/components/icons';
import { i18n } from '@/config/i18n';
import { theme } from '@/config/theme';

export interface ImageUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUpload: (file: File) => Promise<void>;
  maxSizeMB?: number;
  acceptedFormats?: string[];
}

const ModalHeader = styled.div`
  border-bottom: 1px solid ${theme.colors.gray[200]};
  padding: 14px 20px;
  margin: -24px -24px 20px -24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const HeaderAccent = styled.div`
  width: 4px;
  height: 18px;
  background-color: ${theme.colors.primary.accent};
  border-radius: 0;
  flex-shrink: 0;
`;

const HeaderText = styled.h3`
  font-size: 15px;
  font-weight: 600;
  color: ${theme.colors.gray[900]};
  margin: 0;
`;

const CloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 4px;
  width: 24px;
  height: 24px;
  line-height: 0;
  transition: all 0.2s ease;
  border-radius: 4px;
  flex-shrink: 0;
  
  svg {
    width: 18px;
    height: 18px;
  }
  
  &:hover {
    background: ${theme.colors.gray[100]};
  }
`;

const UploadArea = styled.div<{ $isDragActive: boolean; $hasError: boolean }>`
  border: 2px dashed ${({ $isDragActive, $hasError }) =>
    $hasError
      ? theme.colors.status.error
      : $isDragActive
      ? theme.colors.primary
      : theme.colors.gray[300]};
  border-radius: 6px;
  padding: 32px 24px;
  text-align: center;
  background-color: ${({ $isDragActive }) =>
    $isDragActive ? theme.colors.gray[50] : theme.colors.white};
  transition: all 0.2s ease;
  
  &:hover {
    border-color: ${theme.colors.primary};
    background-color: ${theme.colors.gray[50]};
  }
`;

const UploadIconWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 12px;

  svg {
    width: 60px;
    height: 60px;
  }
`;

const UploadText = styled.p`
  font-size: 15px;
  font-weight: 500;
  color: ${theme.colors.gray[700]};
  margin-bottom: 6px;
`;

const UploadSubtext = styled.p`
  font-size: 13px;
  color: ${theme.colors.gray[500]};
  margin-bottom: 12px;
`;

const FileInfo = styled.p`
  font-size: 12px;
  color: ${theme.colors.gray[500]};
  margin-top: 12px;
  text-align: left;
  padding-left: 0;
`;

const FileInfoMetaRow = styled.span`
  display: flex;
  align-items: center;
  gap: 24px;
  flex-wrap: wrap;
`;

const FileInfoMetaText = styled.span`
  display: inline-block;
`;

const FileInfoHelperText = styled.span`
  display: block;
  margin-top: 8px;
`;

const SelectButton = styled(Button)`
  margin: 0 auto;
  background: #3E79E9;
  border-color: #3E79E9;
  box-shadow: none;
  transform: none;

  &:hover:not(:disabled),
  &:focus:not(:disabled),
  &:focus-visible:not(:disabled),
  &:active:not(:disabled) {
    background: #3E79E9;
    border-color: #3E79E9;
    box-shadow: none;
    transform: none;
    outline: none;
  }
`;

const HiddenFileInput = styled.input`
  display: none;
`;

const ErrorText = styled.p`
  color: ${theme.colors.status.error};
  font-size: 13px;
  margin-top: 12px;
  text-align: center;
  font-weight: 500;
`;

export const ImageUploadModal: React.FC<ImageUploadModalProps> = ({
  isOpen,
  onClose,
  onUpload,
  maxSizeMB = 5,
  acceptedFormats = ['image/png', 'image/svg+xml'],
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isDragActive, setIsDragActive] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const maxSizeBytes = maxSizeMB * 1024 * 1024;

  const validateFile = useCallback(
    (file: File): string | null => {
      // Check file type
      if (!acceptedFormats.includes(file.type)) {
        return i18n.settings.organizationSettings.messages.invalidImageFormat;
      }

      // Check file size
      if (file.size > maxSizeBytes) {
        return i18n.settings.organizationSettings.messages.imageTooLarge;
      }

      return null;
    },
    [acceptedFormats, maxSizeBytes]
  );

  const handleFileSelect = useCallback(
    async (file: File) => {
      setError(null);

      const validationError = validateFile(file);
      if (validationError) {
        setError(validationError);
        return;
      }

      // Auto-upload immediately when file is selected
      setIsUploading(true);
      setSelectedFile(file);

      try {
        await onUpload(file);
        handleClose();
      } catch (err) {
        setError(
          err instanceof Error
            ? err.message
            : i18n.settings.organizationSettings.messages.imageUploadFailed
        );
      } finally {
        setIsUploading(false);
      }
    },
    [validateFile, onUpload]
  );

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDragIn = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setIsDragActive(true);
    }
  }, []);

  const handleDragOut = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragActive(false);

      if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
        const file = e.dataTransfer.files[0];
        handleFileSelect(file);
      }
    },
    [handleFileSelect]
  );

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files && e.target.files.length > 0) {
        const file = e.target.files[0];
        handleFileSelect(file);
      }
    },
    [handleFileSelect]
  );

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    setError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleClose = () => {
    setSelectedFile(null);
    setError(null);
    setIsDragActive(false);
    setIsUploading(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="" maxWidth="560px">
      <ModalHeader>
        <HeaderLeft>
          <HeaderAccent />
          <HeaderText>Upload Image</HeaderText>
        </HeaderLeft>
        <CloseButton onClick={handleClose}>
          <CloseIconRed />
        </CloseButton>
      </ModalHeader>
      
      <HiddenFileInput
        ref={fileInputRef}
        type="file"
        accept={acceptedFormats.join(',')}
        onChange={handleInputChange}
      />
      <UploadArea
        $isDragActive={isDragActive}
        $hasError={!!error}
        onDragEnter={handleDragIn}
        onDragLeave={handleDragOut}
        onDragOver={handleDrag}
        onDrop={handleDrop}
      >
        <UploadIconWrapper>
          <UploadIcon size={60} />
        </UploadIconWrapper>

        <UploadText>Drag & Drop an Image Here</UploadText>
        <UploadSubtext>or</UploadSubtext>

        <SelectButton
          variant="primary"
          size="medium"
          type="button"
          onClick={handleUploadClick}
          disabled={isUploading}
        >
          {isUploading ? i18n.common.messages.saving : 'Select an image from your computer'}
        </SelectButton>
      </UploadArea>

      <FileInfo>
        <FileInfoMetaRow>
          <FileInfoMetaText>
            {i18n.settings.organizationSettings.messages.imageUploadSupportedFormatsText}
          </FileInfoMetaText>
          <FileInfoMetaText>
            {i18n.settings.organizationSettings.messages.imageUploadMaximumSizeText}
          </FileInfoMetaText>
        </FileInfoMetaRow>
        <FileInfoHelperText>
          {i18n.settings.organizationSettings.messages.imageUploadHelperText}
        </FileInfoHelperText>
      </FileInfo>

      {error && <ErrorText>{error}</ErrorText>}
    </Modal>
  );
};
